import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Button, Spin, Tooltip, message as antMessage } from "antd";
import { AnimatePresence, motion } from "framer-motion";
import {
  CloseOutlined,
  DownloadOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import { askAiTokens as t } from "./tokens";
import { fileIconFor } from "./fileUtils";

// ─────────────────────────────────────────────────────────────────────────────
// Context — components nested under <DocViewerProvider> can call openDoc()
// to slide in the right-rail preview without prop drilling.
// ─────────────────────────────────────────────────────────────────────────────

interface DocViewerCtx {
  openDoc: (url: string, filename: string) => void;
}

const DocViewerContext = createContext<DocViewerCtx>({
  // Default fallback when no provider is mounted — opens the URL in a new
  // tab instead of silently no-op'ing.
  openDoc: (url: string) => {
    if (typeof window !== "undefined" && url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  },
});

export const useDocViewer = () => useContext(DocViewerContext);

// ─────────────────────────────────────────────────────────────────────────────
// File-type detection
// ─────────────────────────────────────────────────────────────────────────────

type ViewerKind = "pdf" | "office" | "image" | "text" | "unsupported";

const OFFICE_EXTS = new Set([
  "xls",
  "xlsx",
  "doc",
  "docx",
  "ppt",
  "pptx",
  "odt",
  "ods",
  "odp",
]);
const IMAGE_EXTS = new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"]);
const TEXT_EXTS = new Set([
  "txt",
  "csv",
  "md",
  "log",
  "json",
  "xml",
  "yaml",
  "yml",
]);

// Parse the `se=` signed-expiry param from an Azure SAS URL. Returns the
// expiry as a Date, or null if the URL doesn't carry one (or is invalid).
function getSasExpiry(url: string): Date | null {
  try {
    const u = new URL(url);
    const se = u.searchParams.get("se");
    if (!se) return null;
    const d = new Date(se);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function isSasExpired(url: string): boolean {
  const exp = getSasExpiry(url);
  return exp !== null && exp.getTime() <= Date.now();
}

// Fetch the file as a blob, then trigger a download via an object-URL'd
// anchor. Works for cross-origin URLs whose direct <a download> attribute
// the browser would otherwise silently ignore. Falls back to opening the
// URL in a new tab when CORS or the network blocks the fetch.
export async function forceDownload(url: string, filename: string) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename || "download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objUrl), 0);
    return true;
  } catch {
    // CORS / network failure — open in a new tab so the user can save it
    // manually (right-click → Save As).
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return false;
  }
}

const extOf = (filename: string): string => {
  const m = filename.toLowerCase().match(/\.([a-z0-9]+)(?:\?|#|$)/);
  return m?.[1] || "";
};

const kindOf = (filename: string): ViewerKind => {
  const ext = extOf(filename);
  if (ext === "pdf") return "pdf";
  if (OFFICE_EXTS.has(ext)) return "office";
  if (IMAGE_EXTS.has(ext)) return "image";
  if (TEXT_EXTS.has(ext)) return "text";
  return "unsupported";
};

// ─────────────────────────────────────────────────────────────────────────────
// Per-type viewer renderers
// ─────────────────────────────────────────────────────────────────────────────

const PdfViewer: React.FC<{ url: string; filename: string }> = ({
  url,
  filename,
}) => {
  const [loading, setLoading] = useState(true);
  const expired = isSasExpired(url);
  if (expired) {
    return <ExpiredLinkCard url={url} filename={filename} />;
  }
  return (
    <ViewerFrame>
      {loading && <ViewerLoader label="Loading PDF…" />}
      <iframe
        title="PDF preview"
        src={url}
        onLoad={() => setLoading(false)}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          background: "#fafbfc",
          opacity: loading ? 0 : 1,
          transition: "opacity 0.2s ease",
        }}
      />
    </ViewerFrame>
  );
};

// Office files (Excel / Word / PowerPoint) — both Google Docs viewer and
// Microsoft Office Online silently fail on Azure SAS URLs (the `/`, `+`,
// `=` chars in the signature get mangled by the embed service's URL
// decoder). True inline rendering needs CORS on the storage account
// (then we'd parse with SheetJS or similar). Until then, route Office
// files to a clean "open or download" card.
const OfficeViewer: React.FC<{ url: string; filename: string }> = ({
  url,
  filename,
}) => {
  if (isSasExpired(url)) {
    return <ExpiredLinkCard url={url} filename={filename} />;
  }
  const ext = extOf(filename);
  const niceLabel = ext.toUpperCase();
  return (
    <UnsupportedFallback
      url={url}
      filename={filename}
      message={`${niceLabel} files open best in their native app. Open this in a new tab to view, or download a local copy.`}
    />
  );
};

// Specific card shown when an Azure SAS URL has timed out. Same shape as
// UnsupportedFallback but the buttons don't open the broken URL — they
// just acknowledge that the user needs to ask again.
const ExpiredLinkCard: React.FC<{ url: string; filename: string }> = ({
  filename,
}) => {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
        padding: 32,
        textAlign: "center",
        background: "#fafbfc",
      }}
    >
      <div style={{ fontSize: 36 }}>{fileIconFor(filename)}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: t.color.text }}>
        {filename}
      </div>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 10px",
          borderRadius: 999,
          background: "#fef3c7",
          border: "1px solid #fde68a",
          color: "#92400e",
          fontSize: 11.5,
          fontWeight: 600,
          letterSpacing: 0.4,
          textTransform: "uppercase",
        }}
      >
        Link expired
      </div>
      <div
        style={{
          fontSize: 12.5,
          color: t.color.textMuted,
          maxWidth: 380,
          lineHeight: 1.55,
        }}
      >
        This source link has expired. Ask the question again in chat to fetch
        a fresh, downloadable link to this file.
      </div>
    </div>
  );
};

const ImageViewer: React.FC<{ url: string; filename: string }> = ({
  url,
  filename,
}) => {
  if (isSasExpired(url)) {
    return <ExpiredLinkCard url={url} filename={filename} />;
  }
  return (
  <div
    style={{
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
      background: "#fafbfc",
      overflow: "auto",
    }}
  >
    <img
      src={url}
      alt={filename}
      style={{
        maxWidth: "100%",
        maxHeight: "100%",
        borderRadius: 8,
        boxShadow: "0 4px 16px -8px rgba(15, 23, 42, 0.2)",
      }}
    />
  </div>
  );
};

const TextViewer: React.FC<{ url: string; filename: string }> = ({
  url,
  filename,
}) => {
  // <iframe> instead of fetch+pre so we don't need CORS on the blob storage
  // (SAS URLs typically don't have CORS configured for cross origins —
  // `fetch()` would fail). Browsers render text/plain and text/csv inline
  // natively in iframes.
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const expired = isSasExpired(url);
  useEffect(() => {
    if (!loading) return;
    const tm = window.setTimeout(() => setFailed(true), 12000);
    return () => window.clearTimeout(tm);
  }, [loading]);
  if (expired) {
    return <ExpiredLinkCard url={url} filename={filename} />;
  }
  if (failed) {
    return (
      <UnsupportedFallback
        url={url}
        filename={filename}
        message="Preview timed out. Open the file in a new tab or download a copy."
      />
    );
  }
  return (
    <ViewerFrame>
      {loading && <ViewerLoader label="Loading file…" />}
      <iframe
        title={`Preview of ${filename}`}
        src={url}
        onLoad={() => setLoading(false)}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          background: "#fff",
          opacity: loading ? 0 : 1,
          transition: "opacity 0.2s ease",
        }}
      />
    </ViewerFrame>
  );
};

const ViewerFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      flex: 1,
      position: "relative",
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
    }}
  >
    {children}
  </div>
);

const ViewerLoader: React.FC<{ label: string; full?: boolean }> = ({
  label,
  full,
}) => (
  <div
    style={{
      position: full ? "static" : "absolute",
      inset: full ? undefined : 0,
      flex: full ? 1 : undefined,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 12,
      background: "#fafbfc",
      zIndex: 1,
    }}
  >
    <Spin />
    <div style={{ fontSize: 12, color: t.color.textMuted }}>{label}</div>
  </div>
);

const UnsupportedFallback: React.FC<{
  url: string;
  filename: string;
  message?: string;
  extra?: React.ReactNode;
}> = ({ url, filename, message, extra }) => (
  <div
    style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 16,
      padding: 32,
      textAlign: "center",
      background: "#fafbfc",
    }}
  >
    <div style={{ fontSize: 36 }}>{fileIconFor(filename)}</div>
    <div style={{ fontSize: 14, fontWeight: 600, color: t.color.text }}>
      {filename}
    </div>
    <div
      style={{
        fontSize: 12.5,
        color: t.color.textMuted,
        maxWidth: 360,
        lineHeight: 1.55,
      }}
    >
      {message ||
        "This file type can't be previewed inline. Open it in a new tab or download a copy."}
    </div>
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
      {extra}
      <Button
        type="default"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        icon={<ExportOutlined />}
      >
        Open in new tab
      </Button>
      <Button
        type="primary"
        onClick={() => forceDownload(url, filename)}
        icon={<DownloadOutlined />}
        style={{
          background: t.color.accent,
          borderColor: t.color.accent,
        }}
      >
        Download
      </Button>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Drawer host
// ─────────────────────────────────────────────────────────────────────────────

// Custom portal-based drawer — bypasses antd Drawer's internal portal so
// we have full control over the stacking context. The AskAI fullscreen
// wrapper (z-index 1050) can't trap a portal that we mount directly to
// document.body.
const DocViewerDrawer: React.FC<{
  url: string;
  filename: string;
  open: boolean;
  onClose: () => void;
}> = ({ url, filename, open, onClose }) => {
  const kind = useMemo(() => kindOf(filename), [filename]);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!url || downloading) return;
    setDownloading(true);
    const ok = await forceDownload(url, filename);
    setDownloading(false);
    if (!ok) {
      antMessage.info(
        "Couldn't download directly — opened in a new tab. Use right-click → Save As to save."
      );
    }
  }, [url, filename, downloading]);

  // Lock body scroll while the drawer is open so background doesn't shift.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (typeof document === "undefined") return null;

  const panel = (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15, 23, 42, 0.45)",
              zIndex: 9000,
            }}
          />
          {/* Panel */}
          <motion.div
            key="panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              width: "min(820px, 70vw)",
              background: "#fff",
              zIndex: 9001,
              boxShadow: "-16px 0 40px -16px rgba(15, 23, 42, 0.25)",
              display: "flex",
              flexDirection: "column",
              fontFamily: t.font.sans,
            }}
            onClick={(e) => e.stopPropagation()}
          >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 16px",
          borderBottom: `1px solid ${t.color.border}`,
          background: "#fff",
        }}
      >
        <span style={{ fontSize: 18, display: "inline-flex" }}>
          {fileIconFor(filename)}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: t.color.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              letterSpacing: -0.1,
            }}
            title={filename}
          >
            {filename}
          </div>
          <div
            style={{
              fontSize: 11,
              color: t.color.textSubtle,
              textTransform: "uppercase",
              letterSpacing: 0.6,
              marginTop: 1,
            }}
          >
            {kind === "office" ? "Document" : kind} preview
          </div>
        </div>
        <Tooltip
          title={
            isSasExpired(url) ? "Link expired — ask again" : "Open in new tab"
          }
        >
          <Button
            type="text"
            icon={<ExportOutlined />}
            href={isSasExpired(url) ? undefined : url}
            target="_blank"
            rel="noopener noreferrer"
            disabled={isSasExpired(url)}
          />
        </Tooltip>
        <Tooltip
          title={
            isSasExpired(url) ? "Link expired — ask again" : "Download"
          }
        >
          <Button
            type="text"
            icon={<DownloadOutlined />}
            loading={downloading}
            onClick={handleDownload}
            disabled={isSasExpired(url)}
          />
        </Tooltip>
        <Tooltip title="Close">
          <Button type="text" icon={<CloseOutlined />} onClick={onClose} />
        </Tooltip>
      </div>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {kind === "pdf" && <PdfViewer url={url} filename={filename} />}
        {kind === "office" && (
          <OfficeViewer url={url} filename={filename} />
        )}
        {kind === "image" && <ImageViewer url={url} filename={filename} />}
        {kind === "text" && <TextViewer url={url} filename={filename} />}
        {kind === "unsupported" && (
          <UnsupportedFallback url={url} filename={filename} />
        )}
      </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(panel, document.body);
};

export const DocViewerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [doc, setDoc] = useState<{ url: string; filename: string } | null>(
    null
  );

  const openDoc = useCallback((url: string, filename: string) => {
    // eslint-disable-next-line no-console
    console.log("[DocViewer] openDoc", { filename });
    setDoc({ url, filename });
    setOpen(true);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const value = useMemo(() => ({ openDoc }), [openDoc]);

  return (
    <DocViewerContext.Provider value={value}>
      {children}
      <DocViewerDrawer
        url={doc?.url || ""}
        filename={doc?.filename || ""}
        open={open && !!doc}
        onClose={close}
      />
    </DocViewerContext.Provider>
  );
};
